import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo, useState} from "react";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  ProductionVolumeSearchCriteria,
  productionVolumesSelector,
  timeGroup,
  useSearchProductionVolumesQuery
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {
  ProductionVolumeDataTable
} from "../../components/data-table/ProductionVolumeDataTable/ProductionVolumeDataTable";
import {FromToDateToolbar} from "../../components/toolbar/FromToDateToolbar/FromToDateToolbar";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {selectUIProductionVolumeSearchFilter} from "../../store/entity/production-volume/selectors";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {useDispatch} from "react-redux";
import {setUIProductionVolumeSearchFilter} from "../../store/entity/production-volume/actions";
import {useNavigate} from "react-router-dom";
import {useProductionVolumeMutation} from "../../hooks/useProductionVolumeMutation";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

export type ProductionVolumeSearchPageProps = {}

export const ProductionVolumeSearchPage = (props: ProductionVolumeSearchPageProps) => {
  const {regionId, appCategory} = useApp();
  const {t} = useTranslation(["common"]);

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const criteria = useOtsUISelector(selectUIProductionVolumeSearchFilter)

  const [access, setAccess] = useState(false);

  const selectProductionVolumes = useMemo(() => {
    return productionVolumesSelector();
  }, []);

  const searchArg: ProductionVolumeSearchCriteria | typeof skipToken = useMemo(() => {
    if (access && regionId && criteria?.fromDttm) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZoneSafely(criteria.toDttm)?.getTime(),
        regionId: regionId,
        categories: appCategory ? [appCategory.code] : undefined,
        meterReadingCriteria: {
          generate: true,
          timeGroup: timeGroup.daily.code,
        }
      }
    } else {
      return skipToken
    }
  }, [criteria, regionId, appCategory, access])

  const {productionVolumes, isFetching, error, refetch} = useSearchProductionVolumesQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      productionVolumes: selectProductionVolumes(result, {regionId: regionId}),
      isFetching: result.isFetching,
      error: result.error,
    })
  });

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    if (fromDate.getTime() === criteria?.fromDttm && toDate?.getTime() === criteria.toDttm) {
      if (refetch) {
        refetch()
      }
    } else {
      dispatch(setUIProductionVolumeSearchFilter({
        ...criteria,
        fromDttm: fromDate.getTime(),
        toDttm: toDate?.getTime()
      }))
    }
  }, [criteria, refetch, dispatch]);
  const {
    deleteProductionVolume,
  } = useProductionVolumeMutation()

  return (
    <ProtectedPage permissions={["productionvolume_view"]} isLoading={isFetching} error={error} onAccess={setAccess}>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.entity.productionvolume.search.title", "Search Production Volumes")}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["edit"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <FromToDateToolbar
                    defaultValues={{
                      fromDate: criteria?.fromDttm ? new Date(criteria.fromDttm) : new Date(),
                      toDate: criteria?.toDttm ? new Date(criteria?.toDttm) : undefined,
                    }}
                    loading={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <ProductionVolumeDataTable
                  withBorder
                  records={productionVolumes}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}
                  onEdit={
                    (record) =>
                      navigate("form", {
                        state: {
                          id: record.id,
                        }
                      })
                  }
                  onDelete={
                    (record) =>
                      deleteProductionVolume(record.id, undefined)
                  }/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  );
};
